import { useLiveEvents } from "../lib/useLiveEvents";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import PatientWaitlist from "../components/PatientWaitlist";
import RescheduleModal from "../components/RescheduleModal";
import LiveQueueBadge from "../components/LiveQueueBadge";
import { HealthTimelineTab, SampleTracker } from "../components/PatientPhaseB";
import { MyMembership } from "./membership/MembershipPages";
import { 
  FileText, 
  Calendar, 
  FlaskConical, 
  Package, 
  Video, 
  Download, 
  Printer, 
  Eye, 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  User, 
  ShieldCheck,
  Building2,
  ExternalLink
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function PatientPortalPage({ onOpenReport, onBookDoctor, section }) {
  const [rescheduling, setRescheduling] = useState(null);
  const { user, openAuth } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(section || "overview"); // "overview", "appointments", "tests", "reports", "packages"
  const embedded = !!section;
  useEffect(() => { if (section) setActiveTab(section); }, [section]);

  useEffect(() => {
    if (user) {
      fetchDashboard();
    } else {
      setLoading(false);
    }
  }, [user]);

  useLiveEvents(["appointment", "diagnostics", "report", "payment", "order", "notification", "physio", "membership"], () => { if (user) fetchDashboard(true); });
  const fetchDashboard = async (quiet = false) => {
    if (quiet !== true) setLoading(true);
    try {
      const res = await axios.get(`${API}/patient/dashboard`, { withCredentials: true });
      setDashboardData(res.data);
    } catch (err) {
      console.error("Error fetching patient dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto my-20 p-8 bg-white rounded-3xl border border-gray-200 shadow-xl text-center space-y-4">
        <div className="w-14 h-14 bg-[#EBF4F1] text-[#0D3B2E] rounded-full flex items-center justify-center mx-auto">
          <ShieldCheck className="w-8 h-8" />
        </div>
        <h2 className="font-serif-luxury text-2xl font-bold text-[#0D3B2E]">
          6SigmaCare Patient Portal
        </h2>
        <p className="text-xs text-gray-500">
          Please sign in to access your digital medical reports, upcoming doctor appointments and health packages.
        </p>
        <button
          onClick={() => openAuth("patient")}
          data-testid="patient-portal-signin-btn"
          className="w-full py-3 bg-[#0D3B2E] text-white rounded-xl text-xs font-semibold hover:bg-[#165644]"
        >
          Sign In to My Health
        </button>
      </div>
    );
  }

  const cancelAppointment = async (id) => {
    if (!window.confirm("Cancel this appointment? Patients on the waitlist will be alerted.")) return;
    try {
      await axios.put(`${API}/patient/appointments/${id}/cancel`, {}, { withCredentials: true });
      fetchDashboard();
    } catch (err) {
      console.error("Cancel failed:", err);
    }
  };

  const { doctor_appointments = [], diagnostic_bookings = [], packages = [], reports = [], stats = {} } = dashboardData || {};

  return (
    <div className={embedded ? "space-y-4 min-w-0" : "max-w-7xl mx-auto px-4 sm:px-8 py-10 space-y-8"} data-testid={embedded ? `account-section-${activeTab}` : "patient-portal"}>
      
      {/* Patient Profile Header Card */}
      {!embedded && <div className="bg-[#0D3B2E] text-white p-6 sm:p-8 rounded-3xl shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-emerald-800 text-amber-300 flex items-center justify-center font-serif-luxury text-2xl font-bold border border-emerald-600">
            {user.name ? user.name[0].toUpperCase() : "P"}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-400 text-[#07221A] px-2 py-0.2 rounded">
                Verified Health Account
              </span>
            </div>
            <h1 className="font-serif-luxury text-2xl sm:text-3xl font-bold text-white mt-1">
              {user.name}
            </h1>
            <p className="text-xs text-emerald-200">
              {user.email} • {user.phone || "+91 98300 12345"} {user.age ? `• ${user.age} Yrs (${user.gender})` : ""}
            </p>
          </div>
        </div>

        {/* Quick Summary Pills */}
        <div className="flex flex-wrap gap-2 text-xs">
          <div className="px-3.5 py-2 bg-emerald-900/60 rounded-xl border border-emerald-700/50">
            <span className="text-gray-300 block text-[10px]">Upcoming OPDs</span>
            <span className="font-bold text-white text-base">{doctor_appointments.length}</span>
          </div>
          <div className="px-3.5 py-2 bg-emerald-900/60 rounded-xl border border-emerald-700/50">
            <span className="text-gray-300 block text-[10px]">Digital Reports</span>
            <span className="font-bold text-amber-300 text-base">{reports.length}</span>
          </div>
          <div className="px-3.5 py-2 bg-emerald-900/60 rounded-xl border border-emerald-700/50">
            <span className="text-gray-300 block text-[10px]">Lab Bookings</span>
            <span className="font-bold text-white text-base">{diagnostic_bookings.length}</span>
          </div>
        </div>
      </div>}

      {/* Tabs Navigation */}
      {!embedded && <div className="flex flex-wrap gap-1 border-b border-gray-200 bg-white rounded-2xl p-1 shadow-sm text-xs font-semibold text-gray-600">
        {[
          { key: "overview", label: "My Health Overview", icon: User },
          { key: "appointments", label: `Doctor Appointments (${doctor_appointments.length})`, icon: Calendar },
          { key: "tests", label: `Diagnostic Bookings (${diagnostic_bookings.length})`, icon: FlaskConical },
          { key: "reports", label: `Digital Medical Reports (${reports.length})`, icon: FileText },
          { key: "packages", label: `Health Packages (${packages.length})`, icon: Package },
          { key: "timeline", label: "Health Timeline", icon: Calendar },
          { key: "family", label: "Family & Membership", icon: User }
        ].map((t) => {
          const IconC = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl whitespace-nowrap transition-all ${
                activeTab === t.key 
                  ? "bg-[#0D3B2E] text-white shadow" 
                  : "hover:bg-gray-100 text-gray-700"
              }`}
            >
              <IconC className="w-4 h-4" />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>}

      {/* Tab 1: Overview */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          
          {/* Latest Verified Reports Callout */}
          <div className="bg-white p-4 sm:p-6 rounded-3xl border border-gray-200 shadow-sm space-y-4">
            <div className="flex flex-wrap justify-between items-center gap-2">
              <h3 className="font-serif-luxury text-xl font-bold text-[#0D3B2E] flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#3B8271]" />
                Recent Verified Clinical Reports
              </h3>
              <button 
                onClick={() => setActiveTab("reports")} 
                className="text-xs font-bold text-[#0D3B2E] hover:underline"
              >
                View All Reports →
              </button>
            </div>

            {reports.length === 0 ? (
              <p className="text-xs text-gray-500 py-4">No published reports yet.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {reports.slice(0, 3).map((rep) => (
                  <div key={rep.id} className="p-4 bg-[#FBF9F4] rounded-2xl border border-emerald-100 flex flex-col justify-between space-y-3">
                    <div>
                      <span className="text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">
                        {rep.category}
                      </span>
                      <h4 className="text-xs font-bold text-gray-900 mt-1">{rep.test_name}</h4>
                      <p className="text-[11px] text-gray-500">Date: {rep.performed_date} • {rep.doctor_or_pathologist}</p>
                    </div>
                    <button
                      onClick={() => onOpenReport(rep)}
                      data-testid={`view-report-btn-${rep.id}`}
                      className="w-full py-2 bg-[#0D3B2E] text-white text-xs font-semibold rounded-xl hover:bg-[#165644] flex items-center justify-center gap-1.5"
                    >
                      <Eye className="w-3.5 h-3.5" /> View Official Report
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming OPD Appointments */}
          <div className="bg-white p-4 sm:p-6 rounded-3xl border border-gray-200 shadow-sm space-y-4">
            <div className="flex flex-wrap justify-between items-center gap-2">
              <h3 className="font-serif-luxury text-xl font-bold text-[#0D3B2E] flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#3B8271]" />
                Upcoming Specialist Appointments
              </h3>
            </div>

            {doctor_appointments.length === 0 ? (
              <p className="text-xs text-gray-500 py-4">No upcoming appointments booked.</p>
            ) : (
              <div className="space-y-3">
                {doctor_appointments.map((appt) => (
                  <div key={appt.id} className="p-4 bg-[#FBF9F4] rounded-2xl border border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-[#0D3B2E]">{appt.doctor_name}</span>
                        <span className="text-[10px] bg-emerald-100 text-emerald-800 px-2 py-0.2 rounded font-semibold">{appt.specialty}</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">
                        <b>{appt.date}</b> at <b>{appt.time_slot}</b> • {appt.consultation_type}
                      </p>
                      <p className="text-[11px] text-gray-500 break-all">Booking ID: <b className="font-mono">{appt.id}</b> • Status: <span className="text-emerald-700 font-bold">{appt.status}</span></p>
                    </div>

                    <div className="flex gap-2">
                      {appt.video_meeting_url && (
                        <a
                          href={appt.video_meeting_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`join-video-btn-${appt.id}`}
                          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-xl flex items-center gap-1.5 shadow"
                        >
                          <Video className="w-3.5 h-3.5" /> Join Telehealth Video Call
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      )}

      {/* Tab 2: Doctor Appointments Full */}
      {activeTab === "appointments" && (
        <>
        <PatientWaitlist onBookHeld={onBookDoctor} />
        <div className="bg-white p-4 sm:p-6 rounded-3xl border border-gray-200 shadow-sm space-y-4">
          <h3 className="font-serif-luxury text-xl font-bold text-gray-900">All Doctor OPD Appointments</h3>
          {doctor_appointments.length === 0 ? (
            <p className="text-xs text-gray-500">No appointments recorded.</p>
          ) : (
            <div className="space-y-3">
              {doctor_appointments.map((appt) => (
                <div key={appt.id} className="p-4 bg-[#FBF9F4] rounded-2xl border border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="space-y-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900">{appt.doctor_name} ({appt.specialty})</p>
                    <p className="text-xs text-gray-600">Date: <b>{appt.date}</b> at <b>{appt.time_slot}</b></p>
                    <p className="text-[11px] text-gray-500">Type: {appt.consultation_type} • Fee: ₹{appt.amount}{appt.is_followup ? " (follow-up rate)" : ""}{appt.coupon_code ? ` • ${appt.coupon_code}` : ""} ({appt.payment_status}) • Token #{appt.token_no || "—"} • <span data-testid={`appt-status-${appt.id}`} className={appt.status === "Cancelled" ? "text-rose-600 font-semibold" : "text-emerald-700 font-semibold"}>{appt.status}</span></p>
                    {appt.date === new Date().toISOString().split("T")[0] && appt.status !== "Cancelled" && <LiveQueueBadge appointment={appt} />}
                  </div>
                  <div className="flex flex-wrap gap-2">
                  {appt.status !== "Cancelled" && appt.date >= new Date().toISOString().split("T")[0] && (
                    <a href={`${API}/calendar/${appt.id}.ics?email=${encodeURIComponent(appt.patient_email)}`} data-testid={`calendar-appt-${appt.id}`} className="px-3 py-1.5 border border-gray-200 text-gray-700 hover:bg-gray-50 text-[11px] font-semibold rounded-lg">Add to calendar</a>
                  )}
                  {["Confirmed", "Checked-In"].includes(appt.status) && appt.date >= new Date().toISOString().split("T")[0] && (
                    <>
                    <button
                      type="button"
                      onClick={() => setRescheduling(appt)}
                      data-testid={`reschedule-appt-${appt.id}`}
                      className="px-3 py-1.5 border border-[#0D3B2E] text-[#0D3B2E] hover:bg-[#EBF4F1] text-[11px] font-semibold rounded-lg"
                    >
                      Reschedule
                    </button>
                    <button
                      type="button"
                      onClick={() => cancelAppointment(appt.id)}
                      data-testid={`cancel-appt-${appt.id}`}
                      className="px-3 py-1.5 border border-rose-200 text-rose-700 hover:bg-rose-50 text-[11px] font-semibold rounded-lg"
                    >
                      Cancel appointment
                    </button>
                    </>
                  )}
                  {["Completed", "Confirmed"].includes(appt.status) && appt.date < new Date().toISOString().split("T")[0] && onBookDoctor && (
                    <button
                      type="button"
                      onClick={() => onBookDoctor({ id: appt.doctor_id, name: appt.doctor_name, specialty: appt.specialty, _lookup: true })}
                      data-testid={`followup-appt-${appt.id}`}
                      className="px-3 py-1.5 bg-[#0D3B2E] text-white hover:bg-[#165644] text-[11px] font-semibold rounded-lg"
                    >
                      Book follow-up (reduced fee)
                    </button>
                  )}
                  </div>
                  {appt.video_meeting_url && (
                    <a
                      href={appt.video_meeting_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-indigo-600 text-white text-xs font-semibold rounded-xl flex items-center gap-1.5"
                    >
                      <Video className="w-3.5 h-3.5" /> Open Video OPD Room
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        </>
      )}

      {/* Tab 3: Diagnostic Bookings */}
      {activeTab === "tests" && (
        <div className="bg-white p-4 sm:p-6 rounded-3xl border border-gray-200 shadow-sm space-y-4">
          <h3 className="font-serif-luxury text-xl font-bold text-gray-900">Diagnostic Investigations Scheduled</h3>
          {diagnostic_bookings.length === 0 ? (
            <p className="text-xs text-gray-500">No test bookings found.</p>
          ) : (
            <div className="space-y-3">
              {diagnostic_bookings.map((b) => (
                <div key={b.id} className="p-4 bg-[#FBF9F4] rounded-2xl border border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <p className="text-sm font-bold text-gray-900">{b.items?.[0]?.name || "Diagnostic Investigation"}</p>
                    <p className="text-xs text-gray-600">Date: <b>{b.date}</b> ({b.time_slot}) • {b.booking_type}</p>
                    <p className="text-[11px] text-gray-500 break-all">Lab Token: <b className="font-mono">{b.id}</b>{b.tat_due_at ? ` • Report due by ${new Date(b.tat_due_at).toLocaleString()}` : ""}</p>
                    <SampleTracker stage={b.sample_stage} />
                  </div>
                  <span className="px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-bold rounded-full self-start sm:self-center shrink-0">
                    {b.status} (₹{b.total_amount})
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab 4: Digital Reports */}
      {activeTab === "timeline" && <HealthTimelineTab />}
      {activeTab === "family" && <MyMembership />}

      {activeTab === "reports" && (
        <div className="bg-white p-4 sm:p-6 rounded-3xl border border-gray-200 shadow-sm space-y-4">
          <h3 className="font-serif-luxury text-xl font-bold text-gray-900">My Medical & Diagnostic Reports</h3>
          {reports.length === 0 ? (
            <p className="text-xs text-gray-500">No reports ready yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reports.map((rep) => (
                <div key={rep.id} className="p-5 bg-[#FBF9F4] rounded-2xl border border-emerald-100 space-y-3">
                  <div className="flex justify-between items-start">
                    <span className="text-[10px] font-bold uppercase bg-[#0D3B2E] text-white px-2 py-0.5 rounded">
                      {rep.category}
                    </span>
                    <span className="text-[10px] text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                      DPDP Encrypted
                    </span>
                  </div>

                  <div>
                    <h4 className="text-sm font-bold text-gray-900">{rep.test_name}</h4>
                    <p className="text-xs text-gray-500">Performed: {rep.performed_date}</p>
                    <p className="text-[11px] text-gray-600 mt-1 line-clamp-2">{rep.overall_interpretation}</p>
                  </div>

                  <button
                    onClick={() => onOpenReport(rep)}
                    data-testid={`portal-report-view-${rep.id}`}
                    className="w-full py-2 bg-[#0D3B2E] text-white text-xs font-semibold rounded-xl hover:bg-[#165644] flex items-center justify-center gap-1.5"
                  >
                    <Eye className="w-3.5 h-3.5" /> View & Print Report
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab 5: Health Packages */}
      {activeTab === "packages" && (
        <div className="bg-white p-4 sm:p-6 rounded-3xl border border-gray-200 shadow-sm space-y-4">
          <h3 className="font-serif-luxury text-xl font-bold text-gray-900">Purchased Health Packages</h3>
          {packages.length === 0 ? (
            <p className="text-xs text-gray-500">No active packages purchased.</p>
          ) : (
            <div className="space-y-3">
              {packages.map((pkg) => (
                <div key={pkg.id} className="p-4 bg-amber-50/60 rounded-2xl border border-amber-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                  <div>
                    <p className="text-sm font-bold text-gray-900">{pkg.package_name}</p>
                    <p className="text-xs text-gray-600">Scheduled: {pkg.date} ({pkg.time_slot})</p>
                    <p className="text-[11px] text-gray-500 break-all">Booking ID: <b className="font-mono">{pkg.id}</b></p>
                  </div>
                  <span className="px-3 py-1 bg-amber-100 text-amber-900 font-bold text-xs rounded-full self-start sm:self-center shrink-0">
                    Confirmed (₹{pkg.amount})
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {rescheduling && (
        <RescheduleModal appointment={rescheduling} onClose={() => setRescheduling(null)} onDone={() => { setRescheduling(null); fetchDashboard(); }} />
      )}
    </div>
  );
}
